import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import VoiceChat from './VoiceChat';
import { getToken } from '../utils/livekitToken';
import { Mic } from 'lucide-react';
import { useVoiceStore, setVoiceStatus } from '../stores/voiceStore';

interface VoiceChatWrapperProps {
  roomName: string;
  userName: string;
  minimal?: boolean;
  listenOnly?: boolean;
  /** id of an element (e.g. in the editor's top bar) to render the voice
   *  controls into, instead of floating bottom-right. */
  slotId?: string;
}

// Voice chat is intentionally manual-join.
// Reasons:
//   1. Browsers block AudioContext creation before a user gesture — auto-connecting
//      causes "AudioContext was not allowed to start" and silent audio.
//   2. Remote audio .play() calls fail silently when the page hasn't received
//      a gesture yet — tracks subscribe but never produce sound.
//   3. Users may not want to be live immediately on entering a world.
//
// Flow: show "Join Voice" button → user clicks → create/resume AudioContext →
//       fetch token → mount VoiceChat (which connects LiveKit).
const VoiceChatWrapper: React.FC<VoiceChatWrapperProps> = ({
  roomName,
  userName,
  minimal,
  listenOnly,
  slotId,
}) => {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useEffect(() => { setSlot(slotId ? document.getElementById(slotId) : null); }, [slotId]);
  const [joined, setJoined] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const [startMuted, setStartMuted] = useState(false);
  const audioCtxRef = useRef<AudioContext | null>(null);

  // Publish availability to the shared voice store (the VR menu reads it).
  useEffect(() => {
    useVoiceStore.setState((s) => ({ mounts: s.mounts + 1, phase: s.phase === 'unavailable' ? 'idle' : s.phase }));
    return () => {
      useVoiceStore.setState((s) => {
        const mounts = Math.max(0, s.mounts - 1);
        return mounts ? { mounts } : { mounts, phase: 'unavailable', error: null, micBlocked: false, needsAudioUnlock: false, pendingJoin: null };
      });
      try { void audioCtxRef.current?.close(); } catch { /* already closed */ }
    };
  }, []);

  // Remote-audio unlock requests (Enter VR click, VR mic button): resume the
  // page AudioContext too, so it isn't left suspended in the headset.
  useEffect(() => {
    const onUnlock = () => {
      try {
        if (!audioCtxRef.current) audioCtxRef.current = new AudioContext();
        if (audioCtxRef.current.state === 'suspended') void audioCtxRef.current.resume().catch(() => {});
      } catch { /* no gesture */ }
    };
    window.addEventListener('gathrxr:voice-unlock', onUnlock);
    return () => window.removeEventListener('gathrxr:voice-unlock', onUnlock);
  }, []);

  const handleJoin = async () => {
    setJoining(true);
    setError(null);
    setVoiceStatus({ phase: 'joining', error: null });

    try {
      // Create / resume AudioContext inside the gesture handler.
      // This unlocks audio playback for the rest of the page session.
      // (A join requested without a gesture — screen share — may stay
      // suspended; VoiceChat then offers its audio-unlock button.)
      try {
        if (!audioCtxRef.current) {
          audioCtxRef.current = new AudioContext();
        }
        if (audioCtxRef.current.state === 'suspended') {
          await audioCtxRef.current.resume();
        }
      } catch { /* no gesture yet */ }

      const newToken = await getToken(roomName, userName);
      setToken(newToken);
      setJoined(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to join voice';
      setError(msg);
      setVoiceStatus({ phase: 'error', error: msg });
      console.error('[VoiceChat] Join failed:', err);
    } finally {
      setJoining(false);
    }
  };

  // Programmatic joins. Screen share joins on the user's behalf, muted: the
  // presenter needs the room to publish into, and viewers need it to receive
  // the share. Entering VR joins muted (the Enter VR click is the gesture that
  // unlocks audio); the VR mic button joins with the mic on ({ muted: false }).
  const joinRef = useRef(handleJoin);
  joinRef.current = handleJoin;
  const joinedRef = useRef(false);
  joinedRef.current = joined || joining;
  const joiningRef = useRef(false);
  joiningRef.current = joining;
  useEffect(() => {
    const onRequest = (e: Event) => {
      const muted = (e as CustomEvent<{ muted?: boolean } | undefined>).detail?.muted ?? true;
      // Joined but the connection failed: fetch a fresh token to retry.
      const failed = joinedRef.current && !joiningRef.current && useVoiceStore.getState().phase === 'error';
      if (joinedRef.current && !failed) return;
      setStartMuted(muted);
      void joinRef.current();
    };
    window.addEventListener('gathrxr:voice-join', onRequest);
    // A join asked for before this mounted (Enter VR during loading).
    const pending = useVoiceStore.getState().pendingJoin;
    if (pending) {
      useVoiceStore.setState({ pendingJoin: null });
      onRequest(new CustomEvent('gathrxr:voice-join', { detail: pending }));
    }
    return () => window.removeEventListener('gathrxr:voice-join', onRequest);
  }, []);

  // Show join button until user explicitly joins
  if (!joined && slot) {
    return createPortal(
      <div className="gx-voice-slotted">
        <button className="btn-glass gx-voice-join" onClick={handleJoin} disabled={joining} title="Talk with people in this world">
          <Mic size={14} /><span>{joining ? 'Joining…' : 'Join Voice'}</span>
        </button>
        {error && <div className="gx-voice-pop gx-voice-pop-err">{error}</div>}
      </div>,
      slot,
    );
  }

  if (!joined) {
    return (
      <div className="gx-voice-float" style={{ position: 'fixed', bottom: 100, right: 20, zIndex: 1000 }}>
        <button
          onClick={handleJoin}
          disabled={joining}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '10px 18px',
            background: joining
              ? 'rgba(0,194,179,0.5)'
              : 'rgba(0,194,179,0.85)',
            border: 'none',
            borderRadius: 24,
            color: '#fff',
            fontFamily: 'sans-serif',
            fontSize: 13,
            fontWeight: 600,
            cursor: joining ? 'default' : 'pointer',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 2px 12px rgba(0,194,179,0.3)',
          }}
        >
          <Mic size={15} />
          {joining ? 'Joining…' : 'Join Voice'}
        </button>

        {error && (
          <div style={{
            marginTop: 6,
            padding: '6px 12px',
            background: 'rgba(255,59,48,0.9)',
            borderRadius: 8,
            color: '#fff',
            fontFamily: 'sans-serif',
            fontSize: 11,
          }}>
            {error}
          </div>
        )}
      </div>
    );
  }

  // Token fetch can lag behind the join click (edge function cold start).
  // Render nothing while waiting — user sees button has disappeared = feedback.
  if (!token) return null;

  return (
    <VoiceChat
      roomName={roomName}
      userName={userName}
      token={token}
      minimal={minimal}
      listenOnly={listenOnly}
      startMuted={startMuted}
      slot={slot}
    />
  );
};

export default VoiceChatWrapper;
